<template>
  <div>
    <div class="wrap-filter mt-12 flex mb-6 sm:flex-col">
      <search-type-filter @handle-select-type="handleSelectType" :search-type="filters.searchType"/>
      <destination-filter
        :country="filters.country"
        :countries="countries"
        @set-destination="setDestination"
      />
    </div>

    <div class="mb-8">
      <tag-filter
        :selected-tag="filters.tag"
        :tags="tagsToShow"
        @handle-select-tag="handleSelectTag"
      />
    </div>

    <template v-if="isLoading">
      <ul class="list-reset row-tight">
        <li
          v-for="item in numberOfLoading()"
          :key="item"
          class="w-1/4 md:w-1/3 sm:w-full col-tight mb-8 sm:mb-5"
        >
          <loading-item />
        </li>
      </ul>
    </template>

    <div v-else-if="results.length">
      <result-section 
        @show-more="page+=1" 
        :page="page" :results="results" 
        :filters="filters"
        :tags="tags"
      />
    </div>

    <empty v-else-if="filters.tag.length && !results.length" />

    <template v-else>
      <speak-with-us class="mt-10" />
    </template>

  </div>
</template>

<script>
import { applyFilterURL, ALL_COUNTRIES } from '../../helpers/collectionHelpers';
import SpeakWithUs from '../Search/SpeakWithUs';
import SearchTypeFilter from './SearchTypeFilter';
import ResultSection from './ResultSection';
import DestinationFilter from './DestinationFilter';
import TagFilter from './TagFilter';
import LoadingItem from './LoadingItem';
import Empty from "./Empty";

function shuffle(array) {
  let currentIndex = array.length,  randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex != 0) {

    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

function isSupplierValid(supplier) {
  const allKeys = Object.keys(supplier);
  const hasPlatformField = allKeys.some(key => key === '__platform_type');
  const hasOdysseyField = allKeys.some(key => key ==='__odyssey_price' || key === '__odyssey_price_per_day');
  const validPrice = supplier.__odyssey_price || supplier.__odyssey_price_per_day
  const isShowing = Number(supplier.__platform_is_showing_in_collection) === 1
  return hasPlatformField && hasOdysseyField && validPrice && isShowing;
}

export default {
  components: {
    Empty,
    SearchTypeFilter,
    DestinationFilter,
    TagFilter,
    SpeakWithUs,
    LoadingItem,
    ResultSection
  },
  data() {
    $.ajax({
      method: 'POST',
      url: '/',
      data: {
        action: 'zuBlu/public/getSearchTags',
        [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
      },
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).then((response) => {
      this.tags = JSON.parse(response);
    });

    return {
      filters: {
        searchType: 'resort',
        country: null, // { label: _c.title, value: _c.slug, id: _c.id }
        tag: [],
      },
      tags: [],
      countries: ALL_COUNTRIES,
      results: [],
      statistics: [],
      isLoading: false,
      page: 1,
    };
  },
  computed: {
    tagsToShow() {
      if (!this.statistics.length) {
        return this.tags;
      }

      const tagSet = new Set();
      for (const supplier of this.statistics) {
        if (Array.isArray(supplier.__platform_search_tags)) {
          for (const tag of supplier.__platform_search_tags) {
            tagSet.add(tag)
          }
        }
      }

      return this.tags.filter((tagObj) => {
        return Array.from(tagSet).includes(tagObj.value);
      });
    }
  },
  mounted() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('country')) {
      let country = searchParams.get('country');
      const selectedCountry = this.countries.find((_c) => _c.slug === country);
      this.filters.country = {
        label: selectedCountry.title,
        value: selectedCountry.slug,
      };
    }

    if (searchParams.has('type')) {
      this.filters.searchType = searchParams.get('type');
    }

    if (searchParams.has('suitable_for')) {
      this.filters.tag = searchParams.get('suitable_for').split(",");
    }

    this.loadSuppliers();
  },
  methods: {
    setDestination(val) {
      this.page = 1;
      if (val.value) {
        this.filters.country = val;
      } else {
        this.filters.country = null;
      }
      this.filters.tag = []
      applyFilterURL(this.filters);
      this.loadSuppliers();
    },
    handleSelectType(val) {
      this.page = 1;
      this.filters = { searchType: val,
        country: null, 
        tag: [],
      }
      applyFilterURL(this.filters);
      this.loadSuppliers();
    },
    handleSelectTag(tag) {
      this.page = 1;
      if (this.filters.tag.includes(tag.value)) {
        this.filters.tag = this.filters.tag.filter(_t => _t !== tag.value);
      } else if(this.filters.tag.length < 3) {
        this.filters.tag.push(tag.value);
      } else {
        return;
      }

      applyFilterURL(this.filters);
      this.loadSuppliers();
    },
    loadStatistics() {
      const searchMethod = {
        resort: this.$algolia.searchAllResorts,
        liveaboard: this.$algolia.searchAllLiveaboards,
      }[this.filters.searchType];

      const facetFilters = [];

      searchMethod({
        facetFilters,
      }).then((resp) => {
         let validResults = resp.hits.filter(isSupplierValid);
        if (this.filters.country) {
          validResults = validResults.filter(_v => {
             return _v.__platform_country === this.filters.country.value
          })
        }
        this.statistics = validResults;
      });
    },
    loadSuppliers() {
      this.loadStatistics();
      this.isLoading = true;
      const searchMethod = {
        resort: this.$algolia.searchAllResorts,
        liveaboard: this.$algolia.searchAllLiveaboards,
      }[this.filters.searchType];

      let facetFilters = [];
      if (this.filters.tag.length) {
        this.filters.tag.forEach(_t => {
           facetFilters.push(`__platform_search_tags:${_t}`);
        })
      }

      searchMethod({
        facetFilters
      }).then((resp) => {
        let validResults = resp.hits.filter(isSupplierValid);
        if (this.filters.country) {
          validResults = validResults.filter(_v => {
            return _v.__platform_country === this.filters.country.value
          })
        }
        this.results = shuffle(validResults);
        this.isLoading = false;
      });
    },
    numberOfLoading() {
      const isDesktop = window.matchMedia("only screen and (min-width: 1201px)")
        .matches;
      const isTablet = window.matchMedia(
        "(min-width: 601px) and (max-width: 1200px)"
      ).matches;
      const isMobile = window.matchMedia("(max-width: 600px)").matches;

      if (isDesktop) return 12;
      else if (isTablet) return 9;
      else if (isMobile) return 3;
      return 12;
    },
  },
};
</script>
